import React, { useState, useMemo, useRef, useCallback, useEffect } from "react";
import { StockChecklistItem, ChecklistColumnConfig, DEFAULT_COLUMN_CONFIG, SKIP_REASON_LABEL, COST_ORIGIN_LABEL } from "@/types/stockChecklist";
import { Button } from "@/components/ui/button";
import { InlineEditCell } from "./InlineEditCell";
import { ImageUploadCell } from "./ImageUploadCell";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Check, Minus, Plus, Loader2, Square, AlertCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { formatCurrency } from "@/utils/formatters";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface EditedItemData {
  counted?: number;
  notes?: string;
  salePrice?: number;
  costPrice?: number;
  priceTableOverrides?: Record<string, number>;
}

interface StockChecklistTableProps {
  items: StockChecklistItem[];
  visibleColumns?: ChecklistColumnConfig;
  onSaveItem: (itemId: string, data: EditedItemData) => Promise<void>;
  onUploadImage?: (itemId: string, productId: string, file: File) => Promise<void>;
  savingItemId: string | null;
  /** A8-i: master ou "Gerenciar compras" informam custo; os demais só veem (D-A8.4) */
  canEditCost?: boolean;
  /** A8-i: confirmar custo cadastrado do item (master, parte de balanço aberto) */
  onConfirmRegisteredCost?: (itemId: string) => Promise<void>;
  /** A7b: Novo P.V. e tabelas só de master ou "Gerenciar compras" (decisão 1); os demais só veem */
  canEditPrice?: boolean;
}

// A7b: data e hora curtas para os balões de "Contado por" e de item achado
function fmtWhen(iso: string | null | undefined) {
  if (!iso) return "";
  return new Date(iso).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
}

const PRICE_LOCK_HINT = 'Só o master ou quem tem "Gerenciar compras" altera preço de venda e tabelas';

// A8-i: origem do custo com cor — cadastrado em amarelo (a confirmar), sem custo em vermelho (D-A8.2)
function CostOriginBadge({ item }: { item: StockChecklistItem }) {
  if (!item.cost_origin) return null;
  const tone =
    item.cost_origin === "cadastrado"
      ? "bg-amber-500/15 text-amber-700 border-amber-500/40"
      : item.cost_origin === "sem_custo"
        ? "bg-red-500/10 text-red-700 border-red-500/40"
        : "bg-muted text-muted-foreground border-border";
  return (
    <Badge variant="outline" className={cn("mt-1 text-[10px] font-normal whitespace-nowrap", tone)}>
      {COST_ORIGIN_LABEL[item.cost_origin]}
    </Badge>
  );
}

// A8-i: alerta de custo sugerido acima do preço de venda (suspeita de custo por embalagem)
function costAboveSale(item: StockChecklistItem) {
  return item.unit_cost != null && item.sale_price != null && Number(item.sale_price) > 0
    && Number(item.unit_cost) > Number(item.sale_price);
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function ItemStatusIcon({ item }: { item: StockChecklistItem }) {
  if (!item.checked) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Square className="h-4 w-4 text-muted-foreground/60" />
        </TooltipTrigger>
        <TooltipContent>Pendente</TooltipContent>
      </Tooltip>
    );
  }
  if (item.difference !== 0 && item.difference !== null && item.difference !== undefined) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <AlertCircle className="h-4 w-4 text-red-500" />
        </TooltipTrigger>
        <TooltipContent>Divergente ({item.difference > 0 ? "+" : ""}{item.difference})</TooltipContent>
      </Tooltip>
    );
  }
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Check className="h-4 w-4 text-green-500" />
      </TooltipTrigger>
      <TooltipContent>Conferido</TooltipContent>
    </Tooltip>
  );
}

// A7a-i: valores da linha. Item contado mostra o que foi anotado na hora da contagem; não contado, os de agora.
function rowNumbers(item: StockChecklistItem) {
  const counted = item.checked && item.system_stock_at_count != null;
  const system = counted ? Number(item.system_stock_at_count) : Number(item.deposit_stock_now ?? item.current_stock ?? 0);
  const customerOut = counted ? item.customer_out_at_count : item.customer_out_now;
  const registered = counted ? item.registered_units_at_count : item.registered_now;
  const expected = system - Number(customerOut ?? 0);
  return { system, customerOut: customerOut ?? null, registered: registered ?? null, expected };
}

function fmtQty(n: number | null | undefined) {
  if (n === null || n === undefined) return "—";
  return Number(n).toLocaleString("pt-BR", { maximumFractionDigits: 3 });
}

function SkipBadge({ item }: { item: StockChecklistItem }) {
  if (!item.skip_reason) return null;
  const { expected, registered } = rowNumbers(item);
  const text = item.skip_reason === "cadastro_diverge"
    ? `Não será acertado: ${SKIP_REASON_LABEL.cadastro_diverge} (esperado ${fmtQty(expected)}, registradas ${fmtQty(registered)})`
    : `Não será acertado: ${SKIP_REASON_LABEL.esperado_negativo}`;
  return (
    <Badge variant="outline" className="mt-1 bg-amber-500/10 text-amber-700 border-amber-500/30 text-[10px] font-normal whitespace-normal">
      {text}
    </Badge>
  );
}

function DiffBadge({ counted, currentStock }: { counted: number | null | undefined; currentStock: number }) {
  if (counted === undefined || counted === null) return <span className="text-muted-foreground/40 text-xs">—</span>;
  const diff = Math.round((counted - currentStock) * 1000) / 1000;
  if (diff === 0) return <Badge variant="outline" className="bg-green-500/10 text-green-600 border-green-500/30 text-xs px-1.5"><Check className="h-3 w-3" /></Badge>;
  if (diff > 0)   return <Badge variant="outline" className="bg-blue-500/10 text-blue-600 border-blue-500/30 text-xs px-1.5"><Plus className="h-3 w-3 mr-0.5" />{diff}</Badge>;
  return              <Badge variant="outline" className="bg-red-500/10 text-red-600 border-red-500/30 text-xs px-1.5"><Minus className="h-3 w-3 mr-0.5" />{Math.abs(diff)}</Badge>;
}

// ─── Component ────────────────────────────────────────────────────────────────

export const StockChecklistTable = ({
  items,
  visibleColumns = DEFAULT_COLUMN_CONFIG,
  onSaveItem,
  onUploadImage,
  savingItemId,
  canEditCost = false,
  onConfirmRegisteredCost,
  canEditPrice = false,
}: StockChecklistTableProps) => {
  const [editedItems, setEditedItems] = useState<Record<string, EditedItemData>>({});
  const [saveStatus, setSaveStatus] = useState<Record<string, "saving" | "saved" | null>>({});

  // Debounce timers keyed by `${itemId}-${field}`
  const debounceRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  // m-CONF.1: gravação pendente por chave de espera; ao sair da tela grava na hora em vez de perder
  const pendingRef = useRef<Record<string, () => void>>({});
  // Saved-indicator cleanup timers
  const savedTimersRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  // Keep a ref to editedItems so callbacks don't stale-close
  const editedItemsRef = useRef(editedItems);

  useEffect(() => { editedItemsRef.current = editedItems; }, [editedItems]);

  useEffect(() => {
    return () => {
      Object.values(debounceRef.current).forEach(clearTimeout);
      Object.values(savedTimersRef.current).forEach(clearTimeout);
      const pending = Object.values(pendingRef.current);
      pendingRef.current = {};
      pending.forEach((save) => save());
    };
  }, []);

  // ── Save trigger ────────────────────────────────────────────────────────────
  const triggerSave = useCallback(
    async (item: StockChecklistItem, field: keyof EditedItemData, value: number | string | Record<string, number>) => {
      const current = editedItemsRef.current[item.id] ?? {};
      // priceTableOverrides must be merged (not replaced) to preserve other tables' values
      const merged: EditedItemData = {
        ...current,
        [field]: field === 'priceTableOverrides'
          ? { ...(current.priceTableOverrides ?? {}), ...(value as Record<string, number>) }
          : value,
      };

      setSaveStatus((prev) => ({ ...prev, [item.id]: "saving" }));
      if (savedTimersRef.current[item.id]) {
        clearTimeout(savedTimersRef.current[item.id]);
      }

      try {
        await onSaveItem(item.id, {
          counted: merged.counted,
          notes: merged.notes,
          salePrice: merged.salePrice,
          costPrice: merged.costPrice,
          priceTableOverrides: merged.priceTableOverrides,
        });

        setEditedItems((prev) => {
          const next = { ...prev };
          delete next[item.id];
          return next;
        });
        setSaveStatus((prev) => ({ ...prev, [item.id]: "saved" }));
        savedTimersRef.current[item.id] = setTimeout(() => {
          setSaveStatus((prev) => ({ ...prev, [item.id]: null }));
        }, 2000);
      } catch {
        setSaveStatus((prev) => ({ ...prev, [item.id]: null }));
      }
    },
    [onSaveItem]
  );

  // ── Auto-save with debounce for numeric fields ──────────────────────────────
  const handleAutoSave = useCallback(
    (item: StockChecklistItem, field: keyof EditedItemData, value: number | string | Record<string, number>) => {
      // Optimistic local state
      setEditedItems((prev) => ({
        ...prev,
        [item.id]: { ...prev[item.id], [field]: value },
      }));

      const isNumeric = field === "counted" || field === "salePrice" || field === "costPrice";
      const debounceKey = `${item.id}-${field}`;

      if (isNumeric) {
        if (debounceRef.current[debounceKey]) clearTimeout(debounceRef.current[debounceKey]);
        const save = () => triggerSave(item, field, value);
        pendingRef.current[debounceKey] = save;
        debounceRef.current[debounceKey] = setTimeout(() => {
          delete debounceRef.current[debounceKey];
          delete pendingRef.current[debounceKey];
          save();
        }, 400);
      } else {
        triggerSave(item, field, value);
      }
    },
    [triggerSave]
  );

  // ── Per price-table save with per-key debounce ──────────────────────────────
  const handlePriceTableSave = useCallback(
    (item: StockChecklistItem, tableId: string, value: number) => {
      // Merge optimistic state for this table key only
      setEditedItems((prev) => ({
        ...prev,
        [item.id]: {
          ...prev[item.id],
          priceTableOverrides: {
            ...(prev[item.id]?.priceTableOverrides ?? {}),
            [tableId]: value,
          },
        },
      }));

      const debounceKey = `${item.id}-pt-${tableId}`;
      if (debounceRef.current[debounceKey]) clearTimeout(debounceRef.current[debounceKey]);
      const save = () => {
        // Read latest merged overrides from ref so we don't send stale partial data
        const latestOverrides = editedItemsRef.current[item.id]?.priceTableOverrides ?? {};
        triggerSave(item, 'priceTableOverrides', { ...latestOverrides, [tableId]: value });
      };
      pendingRef.current[debounceKey] = save;
      debounceRef.current[debounceKey] = setTimeout(() => {
        delete debounceRef.current[debounceKey];
        delete pendingRef.current[debounceKey];
        save();
      }, 400);
    },
    [triggerSave]
  );

  // Derived per-row values
  const getCountedDisplay = (item: StockChecklistItem) =>
    editedItems[item.id]?.counted ?? item.counted_stock;

  const getStatus = (itemId: string) =>
    saveStatus[itemId] ?? (savingItemId === itemId ? "saving" : null);

  // A7a-i: "Com cliente" só quando algum item tem; "Registradas" só para equipamento no depósito principal
  const showCustomerOut = items.some((i) => (rowNumbers(i).customerOut ?? 0) > 0);
  const showRegistered = items.some((i) => rowNumbers(i).registered != null);

  return (
    <TooltipProvider delayDuration={300}>
      <div className="border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                {/* Fixed columns */}
                <TableHead className="w-[40px] text-center">
                  <Tooltip><TooltipTrigger asChild><span className="cursor-default">St.</span></TooltipTrigger><TooltipContent>Status</TooltipContent></Tooltip>
                </TableHead>
                <TableHead className="w-[100px] font-semibold">SKU</TableHead>
                <TableHead className="min-w-[200px] font-semibold">Nome</TableHead>
                <TableHead className="w-[80px] text-center font-semibold">
                  <Tooltip><TooltipTrigger asChild><span className="cursor-default">Sistema</span></TooltipTrigger><TooltipContent>Saldo do depósito (na hora da contagem, se já contado)</TooltipContent></Tooltip>
                </TableHead>
                {showCustomerOut && (
                  <TableHead className="w-[80px] text-center font-semibold">
                    <Tooltip><TooltipTrigger asChild><span className="cursor-default">Com cliente</span></TooltipTrigger><TooltipContent>Com cliente sem baixa no estoque (contratos e romaneios antigos)</TooltipContent></Tooltip>
                  </TableHead>
                )}
                <TableHead className="w-[80px] text-center font-semibold">Esperado</TableHead>
                {showRegistered && <TableHead className="w-[80px] text-center font-semibold">Registradas</TableHead>}
                <TableHead className="w-[90px] text-center font-semibold">Contado</TableHead>
                <TableHead className="w-[70px] text-center font-semibold">Acerto</TableHead>
                <TableHead className="w-[110px] font-semibold">Contado por</TableHead>

                {/* Optional informative */}
                {visibleColumns.unit     && <TableHead className="w-[80px] text-center font-semibold">Un.</TableHead>}
                {visibleColumns.category && <TableHead className="w-[120px] font-semibold">Categoria</TableHead>}
                {visibleColumns.ean      && <TableHead className="w-[130px] font-semibold">Cód. Barras</TableHead>}

                {/* Optional edit pairs */}
                {visibleColumns.sale_price && (
                  <>
                    <TableHead className="w-[85px] text-right font-semibold text-muted-foreground">P. Venda</TableHead>
                    <TableHead className="w-[85px] text-right font-semibold">Novo P.V.</TableHead>
                  </>
                )}
                {visibleColumns.cost_price && (
                  <>
                    <TableHead className="w-[110px] text-right font-semibold text-muted-foreground">Custo sugerido</TableHead>
                    <TableHead className="w-[110px] text-right font-semibold">Custo</TableHead>
                  </>
                )}

                {/* Per-table dual columns */}
                {(visibleColumns.price_tables ?? []).map((table) => (
                  <React.Fragment key={table.id}>
                    <TableHead className="w-[85px] text-right font-semibold text-muted-foreground">
                      {table.name} At.
                    </TableHead>
                    <TableHead className="w-[95px] text-right font-semibold">
                      {table.name} Novo
                    </TableHead>
                  </React.Fragment>
                ))}

                {/* Photo */}
                {visibleColumns.photo && <TableHead className="w-[60px] text-center font-semibold">Foto</TableHead>}

                {/* Save indicator */}
                <TableHead className="w-[36px]" />
              </TableRow>
            </TableHeader>

            <TableBody>
              {items.map((item) => {
                const counted = getCountedDisplay(item);
                const status  = getStatus(item.id);
                const nums    = rowNumbers(item);

                return (
                  <TableRow
                    key={item.id}
                    className={cn(
                      "transition-colors",
                      item.checked && item.difference === 0 && "bg-green-500/5",
                      item.checked && item.difference !== 0 && item.difference !== null && "bg-red-500/5"
                    )}
                  >
                    {/* Status */}
                    <TableCell className="text-center">
                      <ItemStatusIcon item={item} />
                    </TableCell>

                    {/* SKU — readonly */}
                    <TableCell>
                      <span className="font-mono text-sm text-muted-foreground">
                        {item.sku ?? "—"}
                      </span>
                    </TableCell>

                    {/* Nome — readonly, truncated */}
                    <TableCell>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="block truncate max-w-[280px] text-sm font-medium">
                            {item.name}
                          </span>
                        </TooltipTrigger>
                        <TooltipContent side="right">{item.name}</TooltipContent>
                      </Tooltip>
                      {/* A7b: item achado na prateleira */}
                      {item.added_at && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Badge variant="outline" className="mt-1 bg-blue-500/10 text-blue-700 border-blue-500/30 text-[10px] font-normal">
                              achado
                            </Badge>
                          </TooltipTrigger>
                          <TooltipContent>
                            Adicionado na contagem{item.added_by_name ? ` por ${item.added_by_name}` : ""} · {fmtWhen(item.added_at)}
                          </TooltipContent>
                        </Tooltip>
                      )}
                      <SkipBadge item={item} />
                    </TableCell>

                    {/* Sistema — saldo do depósito (anotado na contagem, ou o de agora) */}
                    <TableCell className="text-center">
                      <span className="font-mono font-semibold text-sm">{fmtQty(nums.system)}</span>
                    </TableCell>

                    {showCustomerOut && (
                      <TableCell className="text-center">
                        <span className="font-mono text-sm text-muted-foreground">
                          {(nums.customerOut ?? 0) > 0 ? fmtQty(nums.customerOut) : "—"}
                        </span>
                      </TableCell>
                    )}

                    {/* Esperado na prateleira */}
                    <TableCell className="text-center">
                      <span className="font-mono text-sm">{fmtQty(nums.expected)}</span>
                    </TableCell>

                    {showRegistered && (
                      <TableCell className="text-center">
                        <span className="font-mono text-sm text-muted-foreground">{fmtQty(nums.registered)}</span>
                      </TableCell>
                    )}

                    {/* Contado — editável, aceita fração */}
                    <TableCell className="text-center">
                      <InlineEditCell
                        value={counted ?? ""}
                        onSave={(val) => handleAutoSave(item, "counted", Number(val))}
                        type="number"
                        placeholder="0"
                        className="w-20 mx-auto bg-primary/5 border-primary/20"
                      />
                    </TableCell>

                    {/* Acerto = contado − esperado */}
                    <TableCell className="text-center">
                      <DiffBadge counted={counted} currentStock={nums.expected} />
                    </TableCell>

                    {/* A7b: quem contou (usuário logado que gravou a contagem) */}
                    <TableCell className="text-sm text-muted-foreground">
                      {item.checked_by_name ? (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="block truncate max-w-[110px]">{item.checked_by_name}</span>
                          </TooltipTrigger>
                          <TooltipContent>{item.checked_by_name} · {fmtWhen(item.checked_at)}</TooltipContent>
                        </Tooltip>
                      ) : "—"}
                    </TableCell>

                    {/* Unidade */}
                    {visibleColumns.unit && (
                      <TableCell className="text-center text-sm text-muted-foreground">
                        {item.unit_name ?? "—"}
                      </TableCell>
                    )}

                    {/* Categoria */}
                    {visibleColumns.category && (
                      <TableCell className="text-sm text-muted-foreground truncate max-w-[120px]">
                        {item.category ?? "—"}
                      </TableCell>
                    )}

                    {/* EAN */}
                    {visibleColumns.ean && (
                      <TableCell className="font-mono text-sm text-muted-foreground">
                        {item.ean ?? "—"}
                      </TableCell>
                    )}

                    {/* Preço Venda — dual pair */}
                    {visibleColumns.sale_price && (
                      <>
                        {/* Current (readonly) */}
                        <TableCell className="text-right">
                          <span className="text-sm text-muted-foreground font-mono">
                            {item.system_sale_price != null
                              ? formatCurrency(item.system_sale_price)
                              : "—"}
                          </span>
                        </TableCell>
                        {/* New (editable) */}
                        <TableCell className="text-right">
                          {canEditPrice ? (
                            <InlineEditCell
                              value={editedItems[item.id]?.salePrice ?? item.sale_price ?? ""}
                              onSave={(val) => handleAutoSave(item, "salePrice", Number(val))}
                              type="number"
                              placeholder="—"
                              className="w-24 bg-primary/5 border-primary/20"
                              align="right"
                            />
                          ) : (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="text-sm font-mono text-muted-foreground">
                                  {item.sale_price != null ? formatCurrency(item.sale_price) : "—"}
                                </span>
                              </TooltipTrigger>
                              <TooltipContent>{PRICE_LOCK_HINT}</TooltipContent>
                            </Tooltip>
                          )}
                        </TableCell>
                      </>
                    )}

                    {/* Preço Custo — dual pair */}
                    {visibleColumns.cost_price && (
                      <>
                        <TableCell className="text-right">
                          <span className="text-sm text-muted-foreground font-mono">
                            {item.system_cost_price != null
                              ? formatCurrency(item.system_cost_price)
                              : "—"}
                          </span>
                          <div><CostOriginBadge item={item} /></div>
                          {costAboveSale(item) && (
                            <div className="mt-1 text-[10px] text-red-600 flex items-center justify-end gap-1">
                              <AlertCircle className="h-3 w-3" /> custo acima do preço de venda
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          {canEditCost ? (
                            <InlineEditCell
                              value={editedItems[item.id]?.costPrice ?? item.unit_cost ?? ""}
                              onSave={(val) => handleAutoSave(item, "costPrice", Number(val))}
                              type="number"
                              placeholder="—"
                              className="w-24 bg-primary/5 border-primary/20"
                              align="right"
                            />
                          ) : (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="text-sm font-mono text-muted-foreground">
                                  {item.unit_cost != null ? formatCurrency(item.unit_cost) : "—"}
                                </span>
                              </TooltipTrigger>
                              <TooltipContent>Só o master ou quem tem "Gerenciar compras" informa custo</TooltipContent>
                            </Tooltip>
                          )}
                          {onConfirmRegisteredCost && item.cost_origin === "cadastrado" && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="mt-1 h-6 px-2 text-[10px]"
                              disabled={savingItemId !== null}
                              onClick={() => onConfirmRegisteredCost(item.id)}
                            >
                              Confirmar
                            </Button>
                          )}
                        </TableCell>
                      </>
                    )}

                    {/* Per-table dual cells */}
                    {(visibleColumns.price_tables ?? []).map((table) => {
                      const currentPrice = item.current_prices_by_table?.[table.id] ?? null;
                      const overridePrice =
                        editedItems[item.id]?.priceTableOverrides?.[table.id] ??
                        item.price_table_overrides?.[table.id];
                      return (
                        <React.Fragment key={`${item.id}-${table.id}`}>
                          <TableCell className="text-right">
                            <span className="text-sm text-muted-foreground font-mono">
                              {currentPrice != null ? formatCurrency(currentPrice) : "—"}
                            </span>
                          </TableCell>
                          <TableCell className="text-right">
                            {canEditPrice ? (
                              <InlineEditCell
                                value={overridePrice ?? ""}
                                onSave={(val) => handlePriceTableSave(item, table.id, Number(val))}
                                type="number"
                                placeholder="—"
                                className="w-24 bg-primary/5 border-primary/20"
                                align="right"
                              />
                            ) : (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className="text-sm font-mono text-muted-foreground">
                                    {overridePrice != null ? formatCurrency(overridePrice) : "—"}
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent>{PRICE_LOCK_HINT}</TooltipContent>
                              </Tooltip>
                            )}
                          </TableCell>
                        </React.Fragment>
                      );
                    })}

                    {/* Foto */}
                    {visibleColumns.photo && (
                      <TableCell className="text-center">
                        <ImageUploadCell
                          currentImageUrl={item.image_url}
                          productName={item.name}
                          disabled={!onUploadImage}
                          onUpload={(file) =>
                            onUploadImage
                              ? onUploadImage(item.id, item.product_id, file)
                              : Promise.resolve()
                          }
                        />
                      </TableCell>
                    )}

                    {/* Save indicator */}
                    <TableCell className="w-[36px]">
                      <div className="flex items-center justify-center h-8 w-8">
                        {status === "saving" && (
                          <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
                        )}
                        {status === "saved" && (
                          <Check className="h-3.5 w-3.5 text-green-500" />
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </div>
    </TooltipProvider>
  );
};
